import type { PhoneModel, SideButton } from "../utils/phoneModels.js";
import styles from "../styles/components/PhoneFrame.module.css";

const BEZEL = 12; // px of bezel around the screen

const BUTTON_SIZES: Record<SideButton, number> = {
  power: 52,
  "volume-up": 36,
  "volume-down": 36,
  "volume-toggle": 56,
  silent: 24,
};

const BUTTON_GAPS: Record<SideButton, number> = {
  power: 100,
  "volume-up": 80,
  "volume-down": 124,
  "volume-toggle": 90,
  silent: 64,
};

function SideButtons({ buttons, side }: { buttons: SideButton[]; side: "left" | "right" }) {
  return (
    <>
      {buttons.map((btn) => (
        <div
          key={btn}
          className={`${styles.btn} ${styles[side]} ${styles[btn.replace("-", "")]}`}
          style={{
            height: BUTTON_SIZES[btn],
            top: BUTTON_GAPS[btn],
          }}
          aria-hidden="true"
        />
      ))}
    </>
  );
}

interface Props {
  model: PhoneModel;
  children: React.ReactNode;
}

export function PhoneFrame({ model, children }: Props) {
  const frameWidth = model.screenWidth + BEZEL * 2;
  const frameHeight = model.screenHeight + BEZEL * 2 + (model.hasHomeButton ? 72 : 0);

  return (
    <div
      className={styles.frame}
      style={{
        width: frameWidth,
        height: frameHeight,
        borderRadius: model.frameRadius,
      }}
      aria-label={`${model.label} preview`}
    >
      <SideButtons buttons={model.buttonsLeft} side="left" />
      <SideButtons buttons={model.buttonsRight} side="right" />

      <div
        className={styles.screen}
        style={{
          width: model.screenWidth,
          height: model.screenHeight,
          borderRadius: model.frameRadius - BEZEL,
        }}
      >
        {/* Top cutout */}
        {model.topCutout === "dynamic-island" && (
          <div className={styles.dynamicIsland} aria-hidden="true" />
        )}
        {model.topCutout === "notch" && (
          <div className={styles.notch} aria-hidden="true" />
        )}
        {model.topCutout === "punch-hole" && (
          <div className={styles.punchHole} aria-hidden="true" />
        )}

        {/* Fake status bar */}
        <div className={styles.statusBar} aria-hidden="true">
          <span className={styles.statusTime}>9:41</span>
          <div className={styles.statusIcons}>
            {/* signal */}
            <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor">
              <rect x="0" y="8" width="3" height="4" rx="0.5" opacity="0.4" />
              <rect x="4.5" y="5" width="3" height="7" rx="0.5" opacity="0.6" />
              <rect x="9" y="2" width="3" height="10" rx="0.5" opacity="0.8" />
              <rect x="13.5" y="0" width="3" height="12" rx="0.5" />
            </svg>
            {/* wifi */}
            <svg width="14" height="12" viewBox="0 0 14 12" fill="currentColor">
              <path d="M7 9.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z" />
              <path d="M3.2 6.8a5.3 5.3 0 0 1 7.6 0l-1.4 1.4a3.3 3.3 0 0 0-4.8 0L3.2 6.8z" />
              <path d="M.4 4a9.3 9.3 0 0 1 13.2 0L12.2 5.4A7.3 7.3 0 0 0 1.8 5.4L.4 4z" />
            </svg>
            {/* battery */}
            <svg width="22" height="12" viewBox="0 0 22 12" fill="currentColor">
              <rect x="0" y="1" width="19" height="10" rx="2" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.5" />
              <rect x="19.5" y="3.5" width="2" height="5" rx="1" opacity="0.5" />
              <rect x="1.5" y="2.5" width="13" height="7" rx="1" />
            </svg>
          </div>
        </div>

        {/* Scrollable content */}
        <div className={styles.content}>
          {children}
        </div>

        {/* Bottom chrome */}
        {model.hasHomeButton ? (
          <div className={styles.homeButton} aria-hidden="true" />
        ) : (
          <div className={styles.homeIndicator} aria-hidden="true" />
        )}
      </div>
    </div>
  );
}
